(() => {
  if (customElements.get('presale-background-video')) return;
  class PresaleBackgroundVideo extends HTMLElement {
    constructor() {
      super();
      this.mediaStates = new WeakMap();
      this.userPaused = false;
    }
    stateFor(video) {
      let state = this.mediaStates.get(video);
      if (state) return state;
      state = { automaticPauses: 0, mayResume: true, restart: true, completed: false };
      this.mediaStates.set(video, state);
      // These follow the media node across synchronous editor reconnects, including
      // pause/play events that were queued before the section was detached.
      video.addEventListener('pause', () => {
        if (state.automaticPauses) state.automaticPauses--;
        else {
          state.mayResume = false;
          if (this.active === video) this.userPaused = true;
        }
        this.updateButton();
      });
      video.addEventListener('play', () => {
        if (video.paused) return;
        state.mayResume = false;
        if (this.active === video) this.userPaused = false;
        delete this.dataset.autoplayBlocked;
        this.updateButton();
      });
      video.addEventListener('ended', () => {
        if (!video.loop) state.completed = true;
        this.updateButton();
      });
      return state;
    }
    pauseVideo(video) {
      if (!video || video.paused) return;
      const state = this.stateFor(video);
      state.automaticPauses++;
      state.mayResume = true;
      video.pause();
    }
    connectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.variants = [...this.querySelectorAll('[data-presale-background-size]')];
      this.variants.forEach(variant => {
        const video = variant.querySelector('video');
        if (!video) return;
        this.stateFor(video);
        video.controls = false;
      });
      this.button = this.querySelector('[data-presale-background-toggle]');
      this.buttonLabel = this.querySelector('[data-presale-video-toggle-label]');
      this.preference = matchMedia('(prefers-reduced-motion: reduce)');
      this.mobile = matchMedia('(max-width: 749px)');
      this.inView = false;
      this.selectVideo();
      this.button?.addEventListener('click', () => this.toggleVideo(), { signal });
      this.mobile.addEventListener('change', () => { this.selectVideo(); this.sync(); }, { signal });
      this.preference.addEventListener('change', () => this.sync(), { signal });
      document.addEventListener('visibilitychange', () => this.sync(), { signal });
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          const entry = entries.find(value => value.target === this);
          if (!entry) return;
          const visible = entry.isIntersecting && entry.intersectionRatio >= .1;
          if (visible && !this.inView && this.active) this.stateFor(this.active).restart = true;
          this.inView = visible;
          this.sync();
        }, { threshold: [0, .1] });
        this.observer.observe(this);
      }
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.variants?.forEach(variant => this.pauseVideo(variant.querySelector('video')));
    }
    selectVideo() {
      const size = this.mobile.matches ? 'mobile' : 'desktop';
      const video = this.variants.find(variant => variant.dataset.presaleBackgroundSize === size)?.querySelector('video');
      if (video !== this.active) {
        this.pauseVideo(this.active);
        this.active = video;
      }
      this.updateButton();
    }
    sync() {
      const video = this.active;
      if (!video || !this.isConnected || this.controller.signal.aborted) return;
      const state = this.stateFor(video);
      if (!this.inView || document.hidden || this.preference.matches) {
        this.pauseVideo(video);
        return;
      }
      if (this.dataset.videoAutoplay !== 'true' || this.userPaused || state.completed || !state.mayResume || !video.paused) return;
      if (state.restart) { video.currentTime = 0; state.restart = false; }
      state.mayResume = false;
      this.playVideo(video);
    }
    playVideo(video) {
      video.play().catch(() => {
        // Keep the poster visible when the browser rejects background playback.
        if (this.active === video && this.isConnected && video.paused) this.dataset.autoplayBlocked = 'true';
        this.updateButton();
      });
      this.updateButton();
    }
    toggleVideo() {
      const video = this.active;
      if (!video) return;
      const state = this.stateFor(video);
      if (video.paused || video.ended) {
        this.userPaused = false;
        // A deliberate Play clears the previous pause intent for both formats.
        // Otherwise returning to a previously paused variant would stay blocked.
        this.variants.forEach(variant => {
          const other = variant.querySelector('video');
          if (!other || other === video) return;
          const otherState = this.stateFor(other);
          otherState.mayResume = true;
          otherState.completed = false;
          otherState.restart = true;
        });
        state.completed = false;
        state.mayResume = false;
        state.restart = false;
        this.playVideo(video);
      } else {
        this.userPaused = true;
        state.mayResume = false;
        video.pause();
      }
      this.updateButton();
    }
    updateButton() {
      if (!this.button || !this.buttonLabel) return;
      this.button.hidden = !this.active;
      this.buttonLabel.textContent = this.active && !this.active.paused ? this.dataset.pauseLabel : this.dataset.playLabel;
    }
  }
  customElements.define('presale-background-video', PresaleBackgroundVideo);
})();
